import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CommentTimeline({ returnId, comments = [], onChange, disabled = false }) {
  const { t, lang } = useLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/returns/${returnId}/comments`, { text: text.trim() });
      onChange && onChange([...(comments || []), data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="comment-timeline">
      {comments && comments.length > 0 && (
        <ol className="relative border-l border-border ml-2 space-y-4 pb-2">
          {[...comments]
            .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
            .map((c) => (
              <li key={c.id} className="ml-4 fade-up" data-testid={`comment-${c.id}`}>
                <div
                  className="absolute -left-[10px] mt-1.5 w-5 h-5 bg-[#0055FF] text-white text-[10px] font-bold flex items-center justify-center"
                  style={{ borderRadius: 4 }}
                >
                  {c.user_initials || (c.user_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-sm font-semibold">{c.user_name}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(c.created_at, lang)}</span>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{c.text}</p>
              </li>
            ))}
        </ol>
      )}

      {!disabled && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("form.commentPlaceholder")}
            rows={2}
            className="flex-1"
            style={{ borderRadius: 4 }}
            data-testid="comment-input"
          />
          <Button
            onClick={submit}
            disabled={busy || !text.trim()}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
            style={{ borderRadius: 4 }}
            data-testid="comment-submit"
          >
            <Send className="w-4 h-4 mr-2" />
            {t("form.addComment")}
          </Button>
        </div>
      )}
    </div>
  );
}
